import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  FormGroup,
  CircularProgress,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import userSupportService from "../../../services/UserSupportService";
import { useFormik } from "formik";
import { LookupUserDTOV1 } from "@beable/roster-backend-client-v1";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "./styles";
import { emptyUserProfile } from '../fixtures';

type ResetPasswordModalProps = {
  profile: LookupUserDTOV1;
  open: boolean;
  handleClose: () => void;
  userRole: string | null;
};

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

export default function ResetPasswordModal(props: ResetPasswordModalProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [profile, setProfile] = useState(emptyUserProfile);
  const [hasErr, setErr] = useState(false);
  const [hasSuccess, setSuccess] = useState(false);
  const [password, setPassword] = useState<string | undefined>();
  const [verified, setVerified] = useState<boolean>(false);
  const [emailLocked, setEmailLocked] = useState<boolean>(true);
  const [confirmEmailOpen, setConfirmEmailOpen] = useState<boolean>(false);

  const validate = useCallback(({ email }: { email: string }) => {
    if (email && !emailRegex.test(email)) {
      return { email: "Invalid email. Please try again." };
    }
    return {};
  }, []);

  useEffect(() => {
    setProfile(props.profile);
  }, [props.profile, props.userRole]);

  const handleSubmit = async () => {
    setErr(false);
    if (formik.values.email) {
      await userSupportService.updateUserEmail(
        props.profile.userAccountId!,
        props.profile.siteId,
        formik.values.email
      );

      profile.email = formik.values.email;
    }

    const success = await userSupportService.resetPassword(
      profile.siteId,
      profile.userId
    );
    if (success.length) {
      setSuccess(true);
      setPassword(success[0]?.temporaryPassword);
    } else {
      setErr(true);
    }
  };

  const formik = useFormik({
    initialValues: { email: "" },
    onSubmit: handleSubmit,
    validate,
  });

  const resetState = async () => {
    setSuccess(false);
    setErr(false);
    setVerified(false);
    setEmailLocked(true);
  };

  const handleChangeVerified = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerified(event.target.checked);
  };

  const unlockPasswordField = () => {
    setEmailLocked(false);
    closeEMailConfirmDialog();
  };

  const closeEMailConfirmDialog = () => {
    setConfirmEmailOpen(false);
  };

  const noEmailOnFile = () => {
    return !profile.email?.trim();
  };

  const disableSave = () => {
    return (
      !verified ||
      formik.isSubmitting ||
      !!password
    );
  };

  return (
    <>
      <Dialog
        aria-labelledby="admin-modal-title"
        aria-describedby="admin-modal-description"
        open={props.open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            props.handleClose();
          }
        }}
        scroll="body"
      >
        <form onSubmit={formik.handleSubmit}>
          <Box sx={styles.paper}>
            <h2 id="admin-modal-title">Reset Password</h2>
            <Typography variant="body2">
              This tool will generate a secure login link to the email
              associated with this user.
            </Typography>
            <FormGroup sx={[styles.mt2, styles.dfr]}>
              <Typography sx={[styles.mt2, styles.mb2]}>
                For security purposes, before resetting the password, please
                confirm the user's information shown below.
              </Typography>
              <Box sx={styles.box}>
                First Name: {profile.firstName} <br />
                Last Name: {profile.lastName} <br />
                Username: {profile.username} <br />
                eMail:{" "}
                {!noEmailOnFile() || !profile.userAccountId || !!password ? (
                  profile.email 
                ) : emailLocked ? (
                  <IconButton
                    aria-label="Enter eMail"
                    size="small"
                    sx={styles.unlockIcon}
                    onClick={() => setConfirmEmailOpen(true)}
                  >
                    <LockIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <>
                    <TextField
                      variant="standard"
                      margin="none"
                      id="email"
                      name="email"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={formik.touched.email && !!formik.errors.email}
                      helperText={formik.touched.email && formik.errors.email}
                      sx={{ width: "275px", '& .MuiInputBase-input': styles.inlineField }}
                    />
                    <IconButton
                      aria-label="Enter eMail"
                      size="small"
                      sx={styles.unlockIcon}
                    >
                      <LockOpenIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
              <Grid container sx={[styles.mt2, styles.mb2]} size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={verified}
                      onChange={handleChangeVerified}
                      name="verified"
                      color="primary"
                    />
                  }
                  label="Verified"
                />
              </Grid>
              <Box sx={{ width: "100%" }}>
                {hasErr ? (
                  <Typography sx={[styles.redTxt, styles.mb2]}>
                    Password reset failed! Re-confirm that a valid email address was entered and try again.
                  </Typography>
                ) : undefined}
                {hasSuccess ? (
                  <Typography sx={[styles.greenTxt, styles.mb2]}>
                    Temporary Password:{" "}
                    <Typography component="span" sx={{ fontWeight: "bold" }}>{password}</Typography>
                    <br />
                    { (noEmailOnFile() && !formik.values.email)
                      ? `
                      Password reset email was not sent because no email address exists. Provide this temp password to the user verbally.
                    `
                      : `
                      Password reset email sent.
                    `}
                  </Typography>
                ) : undefined}
              </Box>
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                disabled={disableSave()}
              >
                {formik.isSubmitting && (
                  <>
                    <CircularProgress data-testid="progress" size={8} />
                    &nbsp;
                  </>
                )}
                Reset Password
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  resetState();
                  props.handleClose();
                }}
                sx={styles.ml2}>
                Close
              </Button>
            </FormGroup>
          </Box>
        </form>
      </Dialog>
      <Dialog
        open={confirmEmailOpen}
        onClose={closeEMailConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Important!</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Only enter an email address given to you by the organization's
            rostering person. Do not accept an email address from a call in
            user.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeEMailConfirmDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={unlockPasswordField}
            autoFocus
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}